/**
 * Wait for in-flight FSRS review writes to commit — ROUTE_INVENTORY §2.11 #9.
 *
 * The runners submit reviews fire-and-forget so mid-session card advance stays
 * instant. That leaves the finish flow with a race: recomputing the daily plan
 * while the writes are still in the air counts the just-reviewed concepts as
 * still due, and the user finishes a session only to see the same "Due today"
 * number they started with.
 *
 * Lifted out of `PracticeArenaPage` because the rule it encodes is about four
 * states a play function cannot reach — nothing in flight, the last one
 * settling, an unrelated mutation settling, and the safety cap expiring — and
 * the page renders none of them differently.
 *
 * The mutation key is a PARAMETER rather than an import: `revisionKeys` lives
 * in `@/hooks`, and `src/lib` must not import hooks (the ESLint layering rule).
 * It also means the "does not resolve on someone else's mutation" case is
 * testable without a review anywhere near the test.
 */

import type { MutationKey, QueryClient } from '@tanstack/react-query';

/**
 * Upper bound on the wait. Reviews normally settle in well under this; the cap
 * exists so one hung request cannot block the "Due today" refresh forever —
 * a stale count is a much smaller failure than a session that never finishes.
 */
export const REVIEW_SETTLE_TIMEOUT_MS = 5000;

export interface WaitForReviewsOptions {
  /** Which mutations to wait on. Typically `revisionKeys.submitReview()`. */
  mutationKey: MutationKey;
  timeoutMs?: number;
}

/**
 * Resolve once no mutation matching `mutationKey` is in flight, or after
 * `timeoutMs`, whichever comes first.
 *
 * Resolves SYNCHRONOUSLY-ish (a already-resolved promise) when nothing is in
 * flight, so the common case — a session where every review landed long ago —
 * costs neither a subscription nor a timer.
 */
export function waitForReviewsToSettle(
  qc: QueryClient,
  { mutationKey, timeoutMs = REVIEW_SETTLE_TIMEOUT_MS }: WaitForReviewsOptions
): Promise<void> {
  if (qc.isMutating({ mutationKey }) === 0) return Promise.resolve();

  return new Promise<void>(resolve => {
    // Both exits — the cache notification and the timeout — run `finish`, and
    // either can arrive after the other. The latch is what stops the timer
    // firing into an already-unsubscribed cache, and what stops a burst of
    // cache notifications resolving more than once.
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const unsubscribe = qc.getMutationCache().subscribe(() => {
      if (qc.isMutating({ mutationKey }) === 0) finish();
    });
    const timer = window.setTimeout(finish, timeoutMs);
  });
}
